import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Mail, Phone } from "lucide-react";
import { CONTACT, mailtoConsult } from "@/lib/contact";

const EASE = [0.16, 1, 0.3, 1];

const inputCls =
    "w-full bg-[#0E121B] border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#FF3B30]/60 focus:ring-2 focus:ring-[#FF3B30]/20 transition-[border-color,box-shadow] duration-300";

export default function Contact() {
    const [submitted, setSubmitted] = useState(false);

    const [form, setForm] = useState({ name: "", email: "", company: "", goal: "" });

    const onSubmit = (e) => {
        e.preventDefault();
        const body = [
            `Name: ${form.name}`,
            `Email: ${form.email}`,
            `Company: ${form.company}`,
            `Goal: ${form.goal}`,
        ].join("\n");
        window.location.href = `${mailtoConsult}&body=${encodeURIComponent(body)}`;
        setSubmitted(true);
    };

    return (
        <section id="contact" data-testid="contact-form-section" className="relative px-4 sm:px-8 lg:px-16 py-24 lg:py-36 overflow-hidden">
            <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full bg-[#FF3B30]/8 blur-[160px] pointer-events-none" />

            <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="lg:col-span-6"
                >
                    <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-semibold mb-4">
                        Book a free consultation
                    </p>
                    <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-6xl text-white leading-[0.95]">
                        Your pipeline
                        <br />
                        <span className="text-stroke-red">audit is free.</span>
                    </h2>
                    <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-lg">
                        Thirty minutes. Zero commitment. Walk away with a custom breakdown of
                        your biggest sales bottlenecks — whether you hire us or not.
                    </p>
                    <a
                        data-testid="contact-email-link"
                        href={mailtoConsult}
                        className="mt-8 inline-flex items-center gap-3 text-sm font-semibold text-slate-300 hover:text-white transition-colors duration-300"
                    >
                        <span className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center">
                            <Mail className="w-4 h-4 text-[#FF3B30]" />
                        </span>
                        Prefer email? {CONTACT.email}
                    </a>
                    <a
                        href={`tel:${CONTACT.phoneTel}`}
                        className="mt-4 inline-flex items-center gap-3 text-sm font-semibold text-slate-300 hover:text-white transition-colors duration-300"
                    >
                        <span className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center">
                            <Phone className="w-4 h-4 text-[#FF3B30]" />
                        </span>
                        {CONTACT.phoneDisplay}
                    </a>
                    <p className="mt-5 text-sm text-slate-500">{CONTACT.address}</p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
                    className="lg:col-span-6"
                >
                    <div className="rounded-3xl border border-white/10 bg-[#0E121B]/75 backdrop-blur-2xl p-8 sm:p-10 shadow-2xl">
                        {submitted ? (
                            <div data-testid="contact-form-success" className="py-16 text-center">
                                <CheckCircle2 className="w-12 h-12 text-[#FF3B30] mx-auto mb-5" />
                                <h3 className="font-display font-bold text-2xl text-white">Your email app should open</h3>
                                <p className="mt-3 text-sm text-slate-400 max-w-sm mx-auto leading-relaxed">
                                    If it doesn&apos;t, email {CONTACT.email} or call {CONTACT.phoneDisplay}.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={onSubmit} className="space-y-5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="contact-name" className="block font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                                            Full name
                                        </label>
                                        <input id="contact-name" data-testid="contact-form-name-input" required placeholder="Jane Tremblay" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                                    </div>
                                    <div>
                                        <label htmlFor="contact-email" className="block font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                                            Company email
                                        </label>
                                        <input id="contact-email" data-testid="contact-form-email-input" type="email" required placeholder="jane@company.ca" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="contact-company" className="block font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                                            Company & team size
                                        </label>
                                        <input id="contact-company" data-testid="contact-form-company-input" required placeholder="Acme Inc — 12 reps" className={inputCls} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                                    </div>
                                    <div>
                                        <label htmlFor="contact-goal" className="block font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                                            Primary growth goal
                                        </label>
                                        <select id="contact-goal" data-testid="contact-form-goal-select" required value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} className={`${inputCls} appearance-none`}>
                                            <option value="" disabled>Select a focus</option>
                                            <option value="analytics">Revenue Analytics</option>
                                            <option value="automation">Sales Automation</option>
                                            <option value="ai">AI Copilots</option>
                                            <option value="all">All-in-One Engine</option>
                                        </select>
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    data-testid="contact-form-submit-button"
                                    className="group w-full flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#ff5147] text-white font-semibold px-7 py-4 rounded-full transition-colors duration-300 shadow-[0_0_28px_rgba(255,59,48,0.35)] hover:shadow-[0_0_48px_rgba(255,59,48,0.6)]"
                                >
                                    Book Free 30-Min Growth Audit
                                    <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                                </button>
                                <p className="text-center text-xs text-slate-500">
                                    No commitment required. Custom sales bottleneck audit provided on the call.
                                </p>
                            </form>
                        )}
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
